import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatRomeDate, formatRome } from '../lib/datetime';
import { api, useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Plus, Calendar as CalendarIcon, Clock, Building2, Repeat,
  Loader2, Trash2, Play, ChevronLeft, ChevronRight, ListChecks, Target, UserCog, Pencil,
  Archive, CheckCircle2
} from 'lucide-react';

const frequencyLabels = {
  daily: 'Giornaliera', weekly: 'Settimanale', biweekly: 'Bisettimanale',
  monthly: 'Mensile', quarterly: 'Trimestrale', semiannual: 'Semestrale', yearly: 'Annuale'
};

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

const ScheduledMaintenancePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [maintenances, setMaintenances] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyHotelFilter, setHistoryHotelFilter] = useState('all');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [hotels, setHotels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMonth, setViewMonth] = useState(new Date());

  const [newMaintenance, setNewMaintenance] = useState({
    title: '', description: '', hotel_id: '', area: '', category_id: '',
    frequency: 'monthly', scheduled_date: new Date().toISOString(),
    assigned_to: '', assigned_to_ids: [], notes: ''
  });

  // === Edit / Reassign dialog ===
  // super_admin + hotel_manager possono modificare titolo, descrizione, data,
  // tecnico assegnato e note. Utile per riassegnare al volo in caso di
  // malattia o riposo del manutentore.
  const isManager = user?.role === 'super_admin' || user?.role === 'hotel_manager';
  const [editDialog, setEditDialog] = useState(null);   // {id, title, description, scheduled_date, assigned_to, notes}
  const [savingEdit, setSavingEdit] = useState(false);

  const openEdit = (m) => {
    setEditDialog({
      id: m.id,
      title: m.title || '',
      description: m.description || '',
      scheduled_date: m.scheduled_date ? new Date(m.scheduled_date).toISOString().slice(0, 16) : '',
      assigned_to: m.assigned_to || 'none',
      assigned_to_ids: m.assigned_to_ids || (m.assigned_to ? [m.assigned_to] : []),
      notes: m.notes || '',
      priority: m.priority || 'media',
    });
  };

  const saveEdit = async () => {
    if (!editDialog) return;
    try {
      setSavingEdit(true);
      const ids = editDialog.assigned_to_ids || [];
      const payload = {
        title: editDialog.title,
        description: editDialog.description,
        scheduled_date: editDialog.scheduled_date ? new Date(editDialog.scheduled_date).toISOString() : undefined,
        assigned_to: ids[0] || null,
        assigned_to_ids: ids,
        notes: editDialog.notes,
        priority: editDialog.priority,
      };
      // remove undefined keys
      Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);
      await api.put(`/scheduled-maintenance/${editDialog.id}`, payload);
      toast.success('Manutenzione aggiornata');
      setEditDialog(null);
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore aggiornamento');
    } finally {
      setSavingEdit(false);
    }
  };

  // ===== Target / Checklist generation =====
  const [hotelTargets, setHotelTargets] = useState(null);
  const [targetsLoading, setTargetsLoading] = useState(false);
  const [targetType, setTargetType] = useState('all_rooms');
  const [targetFloor, setTargetFloor] = useState('');
  const [customRooms, setCustomRooms] = useState([]); // array di numeri camera
  const [customAreas, setCustomAreas] = useState([]);
  const [manualItemsText, setManualItemsText] = useState('');

  // Quando cambia hotel_id nel form, carica camere/aree disponibili
  useEffect(() => {
    const hid = newMaintenance.hotel_id;
    if (!hid) {
      setHotelTargets(null);
      return;
    }
    let cancelled = false;
    setTargetsLoading(true);
    api.get(`/scheduled-maintenance/hotel/${hid}/targets`)
      .then(res => { if (!cancelled) setHotelTargets(res.data); })
      .catch(() => { if (!cancelled) setHotelTargets(null); })
      .finally(() => { if (!cancelled) setTargetsLoading(false); });
    return () => { cancelled = true; };
  }, [newMaintenance.hotel_id]);

  // Reset selezioni custom quando cambia targetType o hotel
  useEffect(() => {
    setCustomRooms([]);
    setCustomAreas([]);
    setTargetFloor('');
    setManualItemsText('');
  }, [targetType, newMaintenance.hotel_id]);

  // Conteggio preview item che verranno creati
  const computePreviewCount = () => {
    if (!hotelTargets) return 0;
    if (targetType === 'all_rooms') return hotelTargets.rooms_count;
    if (targetType === 'common_areas') return hotelTargets.common_areas_count;
    if (targetType === 'rooms_and_areas') return hotelTargets.rooms_count + hotelTargets.common_areas_count;
    if (targetType === 'floor') {
      if (!targetFloor) return 0;
      return hotelTargets.rooms.filter(r => String(r.floor) === String(targetFloor)).length;
    }
    if (targetType === 'custom') return customRooms.length + customAreas.length;
    if (targetType === 'manual') {
      return manualItemsText.split('\n').map(s => s.trim()).filter(Boolean).length;
    }
    if (targetType === 'none') return 0;
    return 0;
  };

  const buildTargetConfig = () => {
    if (targetType === 'none') return null;
    if (targetType === 'floor') return { type: 'floor', floor: parseInt(targetFloor || 1) };
    if (targetType === 'custom') return { type: 'custom', rooms: customRooms, areas: customAreas };
    if (targetType === 'manual') {
      return {
        type: 'manual',
        manual_items: manualItemsText.split('\n').map(s => s.trim()).filter(Boolean),
      };
    }
    return { type: targetType };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [maintenancesRes, hotelsRes, categoriesRes] = await Promise.all([
        api.get('/scheduled-maintenance'), api.get('/hotels'), api.get('/categories')
      ]);
      setMaintenances(maintenancesRes.data);
      setHotels(hotelsRes.data);
      setCategories(categoriesRes.data);
      try {
        const usersRes = await api.get('/users');
        setTechnicians(usersRes.data.filter(u => u.role === 'technician'));
      } catch (e) {
        console.log('Could not fetch technicians list');
      }
    } catch (err) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Storico manutenzioni completate (rispetta RBAC backend, manager vede solo i suoi hotel)
  const fetchHistory = async (hotelId) => {
    try {
      setHistoryLoading(true);
      const params = hotelId && hotelId !== 'all' ? { hotel_id: hotelId } : {};
      const { data } = await api.get('/scheduled-maintenance/history', { params });
      setHistory(data);
    } catch (e) {
      // silenzioso: lo storico potrebbe essere vuoto
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchHistory(historyHotelFilter);
  }, [historyHotelFilter, user]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const target_config = buildTargetConfig();
      const previewCount = computePreviewCount();
      if (target_config && previewCount === 0 && targetType !== 'none') {
        toast.error('Il target selezionato non genera nessun elemento. Verifica la selezione.');
        return;
      }
      await api.post('/scheduled-maintenance', { ...newMaintenance, target_config });
      toast.success(target_config ? `Manutenzione creata con ${previewCount} elementi` : 'Manutenzione programmata creata');
      setCreateOpen(false);
      setNewMaintenance({
        title: '', description: '', hotel_id: '', area: '', category_id: '',
        frequency: 'monthly', scheduled_date: new Date().toISOString(),
        assigned_to: '', assigned_to_ids: [], notes: ''
      });
      setTargetType('all_rooms');
      fetchData();
    } catch (err) {
      toast.error('Errore nella creazione');
    }
  };

  const handleExecute = async (id) => {
    try {
      await api.post(`/scheduled-maintenance/${id}/execute`);
      toast.success('Ticket creato dalla manutenzione programmata');
      fetchData();
    } catch (err) {
      toast.error("Errore nell'esecuzione");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa manutenzione?')) return;
    try {
      await api.delete(`/scheduled-maintenance/${id}`);
      toast.success('Manutenzione eliminata');
      fetchData();
    } catch (err) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const getHotelName = (hotelId) => hotels.find(h => h.id === hotelId)?.name || 'N/A';

  // Custom calendar grid logic
  const getCalendarDays = () => {
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    let startDay = firstDay.getDay() - 1; // Monday = 0
    if (startDay < 0) startDay = 6;
    const days = [];
    // Days from prev month
    for (let i = startDay - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      days.push({ date: d, isCurrentMonth: false });
    }
    // Current month days
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push({ date: new Date(year, month, d), isCurrentMonth: true });
    }
    // Fill remaining to complete weeks
    while (days.length % 7 !== 0) {
      const nextDate = new Date(year, month + 1, days.length - lastDay.getDate() - startDay + 1);
      days.push({ date: nextDate, isCurrentMonth: false });
    }
    return days;
  };

  const maintenanceDateMap = {};
  maintenances.forEach(m => {
    const key = new Date(m.scheduled_date).toDateString();
    if (!maintenanceDateMap[key]) maintenanceDateMap[key] = [];
    maintenanceDateMap[key].push(m);
  });

  const isToday = (date) => date.toDateString() === new Date().toDateString();
  const isSelected = (date) => date.toDateString() === selectedDate.toDateString();
  const hasEvents = (date) => !!maintenanceDateMap[date.toDateString()];

  const prevMonth = () => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1));
  const nextMonth = () => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1));

  const selectedDateMaintenances = maintenanceDateMap[selectedDate.toDateString()] || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
      </div>
    );
  }

  const calendarDays = getCalendarDays();

  return (
    <div className="space-y-6" data-testid="scheduled-maintenance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900">Manutenzioni Programmate</h1>
          <p className="text-slate-500 mt-1">Calendario e pianificazione manutenzioni</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-900 hover:bg-blue-800 gap-2" data-testid="create-maintenance-button">
              <Plus className="w-4 h-4" /> Nuova Programmazione
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">Nuova Manutenzione Programmata</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Titolo *</Label>
                <Input value={newMaintenance.title} onChange={(e) => setNewMaintenance({...newMaintenance, title: e.target.value})} placeholder="es. Controllo impianto HVAC" required />
              </div>
              <div className="space-y-2">
                <Label>Descrizione</Label>
                <Textarea value={newMaintenance.description} onChange={(e) => setNewMaintenance({...newMaintenance, description: e.target.value})} placeholder="Dettagli della manutenzione..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Hotel *</Label>
                  <Select value={newMaintenance.hotel_id} onValueChange={(v) => setNewMaintenance({...newMaintenance, hotel_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Seleziona hotel" /></SelectTrigger>
                    <SelectContent>
                      {hotels.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Area *</Label>
                  <Input value={newMaintenance.area} onChange={(e) => setNewMaintenance({...newMaintenance, area: e.target.value})} placeholder="es. Tutto l'hotel, Piano 3" required />
                </div>
              </div>

              {/* ===== TARGET / CHECKLIST ===== */}
              {newMaintenance.hotel_id && (
                <div className="border border-amber-200 bg-amber-50/40 rounded-lg p-3 space-y-3" data-testid="target-config-section">
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-amber-700" />
                    <Label className="text-amber-900 font-semibold">Target — Checklist da generare</Label>
                  </div>
                  {targetsLoading ? (
                    <div className="text-xs text-slate-500 flex items-center gap-2">
                      <Loader2 className="w-3 h-3 animate-spin" /> Carico camere e aree...
                    </div>
                  ) : !hotelTargets ? (
                    <div className="text-xs text-red-600">Impossibile caricare camere/aree per questo hotel.</div>
                  ) : (
                    <>
                      <div className="text-[11px] text-slate-600">
                        Disponibili: <strong>{hotelTargets.rooms_count}</strong> camere su {hotelTargets.floors.length} piani · <strong>{hotelTargets.common_areas_count}</strong> aree comuni
                      </div>
                      <Select value={targetType} onValueChange={setTargetType}>
                        <SelectTrigger data-testid="target-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all_rooms">Tutte le camere ({hotelTargets.rooms_count})</SelectItem>
                          <SelectItem value="common_areas">Tutte le aree comuni ({hotelTargets.common_areas_count})</SelectItem>
                          <SelectItem value="rooms_and_areas">Camere + Aree comuni ({hotelTargets.rooms_count + hotelTargets.common_areas_count})</SelectItem>
                          <SelectItem value="floor">Solo un piano specifico</SelectItem>
                          <SelectItem value="custom">Selezione personalizzata</SelectItem>
                          <SelectItem value="manual">Lista manuale (testo libero)</SelectItem>
                          <SelectItem value="none">Nessuna checklist (manutenzione semplice)</SelectItem>
                        </SelectContent>
                      </Select>

                      {targetType === 'floor' && (
                        <Select value={String(targetFloor)} onValueChange={setTargetFloor}>
                          <SelectTrigger data-testid="target-floor-select"><SelectValue placeholder="Seleziona piano" /></SelectTrigger>
                          <SelectContent>
                            {hotelTargets.floors.map(f => (
                              <SelectItem key={f} value={String(f)}>
                                Piano {f} ({hotelTargets.rooms.filter(r => r.floor === f).length} camere)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}

                      {targetType === 'custom' && (
                        <div className="space-y-2">
                          <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">Camere</div>
                          <div className="max-h-32 overflow-y-auto border border-slate-200 rounded p-2 grid grid-cols-4 gap-1 bg-white" data-testid="custom-rooms-grid">
                            {hotelTargets.rooms.map(r => {
                              const checked = customRooms.includes(r.number);
                              return (
                                <label key={r.number} className={`flex items-center gap-1 text-xs cursor-pointer p-1 rounded ${checked ? 'bg-blue-100' : 'hover:bg-slate-50'}`}>
                                  <Checkbox checked={checked} onCheckedChange={(v) => {
                                    setCustomRooms(prev => v ? [...prev, r.number] : prev.filter(x => x !== r.number));
                                  }} />
                                  <span>{r.number}</span>
                                </label>
                              );
                            })}
                          </div>
                          {hotelTargets.common_areas_count > 0 && (
                            <>
                              <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide mt-2">Aree comuni</div>
                              <div className="grid grid-cols-2 gap-1">
                                {hotelTargets.common_areas.map(a => {
                                  const checked = customAreas.includes(a);
                                  return (
                                    <label key={a} className={`flex items-center gap-1 text-xs cursor-pointer p-1 rounded ${checked ? 'bg-blue-100' : 'hover:bg-slate-50'}`}>
                                      <Checkbox checked={checked} onCheckedChange={(v) => {
                                        setCustomAreas(prev => v ? [...prev, a] : prev.filter(x => x !== a));
                                      }} />
                                      <span>{a}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </>
                          )}
                        </div>
                      )}

                      {targetType === 'manual' && (
                        <Textarea
                          value={manualItemsText}
                          onChange={(e) => setManualItemsText(e.target.value)}
                          placeholder="Un elemento per riga&#10;es:&#10;Macchina caffè bar piano terra&#10;Macchina caffè ristorante&#10;Frigorifero cucina"
                          rows={4}
                          data-testid="manual-items-textarea"
                        />
                      )}

                      {targetType !== 'none' && (
                        <div className="bg-white border border-amber-300 rounded px-3 py-2 text-sm flex items-center gap-2" data-testid="target-preview-count">
                          <ListChecks className="w-4 h-4 text-amber-700" />
                          <span className="text-slate-700">Verranno creati</span>
                          <span className="font-bold text-amber-900">{computePreviewCount()}</span>
                          <span className="text-slate-700">elementi nella checklist</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Categoria</Label>
                  <Select value={newMaintenance.category_id} onValueChange={(v) => setNewMaintenance({...newMaintenance, category_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Seleziona categoria" /></SelectTrigger>
                    <SelectContent>
                      {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Frequenza *</Label>
                  <Select value={newMaintenance.frequency} onValueChange={(v) => setNewMaintenance({...newMaintenance, frequency: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Giornaliera</SelectItem>
                      <SelectItem value="weekly">Settimanale</SelectItem>
                      <SelectItem value="biweekly">Bisettimanale</SelectItem>
                      <SelectItem value="monthly">Mensile</SelectItem>
                      <SelectItem value="quarterly">Trimestrale</SelectItem>
                      <SelectItem value="semiannual">Semestrale</SelectItem>
                      <SelectItem value="yearly">Annuale</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Data Prossima Esecuzione *</Label>
                <Input type="datetime-local" value={newMaintenance.scheduled_date.slice(0, 16)} onChange={(e) => setNewMaintenance({...newMaintenance, scheduled_date: new Date(e.target.value).toISOString()})} required />
              </div>
              <div className="space-y-2">
                <Label>Assegna a Tecnici <span className="text-xs text-slate-500 font-normal">(selezione multipla)</span></Label>
                <div className="border border-slate-200 rounded-md max-h-48 overflow-y-auto p-2 space-y-1 bg-white" data-testid="assignee-multi-list">
                  {technicians.length === 0 && <p className="text-xs text-slate-400 p-2">Nessun tecnico disponibile</p>}
                  {technicians.map(t => {
                    const selected = (newMaintenance.assigned_to_ids || []).includes(t.id);
                    return (
                      <label
                        key={t.id}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm ${selected ? 'bg-blue-50 border border-blue-200' : 'hover:bg-slate-50 border border-transparent'}`}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={(e) => {
                            const cur = newMaintenance.assigned_to_ids || [];
                            const next = e.target.checked ? [...cur, t.id] : cur.filter(x => x !== t.id);
                            setNewMaintenance({ ...newMaintenance, assigned_to_ids: next, assigned_to: next[0] || '' });
                          }}
                          className="rounded border-slate-300"
                          data-testid={`tech-checkbox-${t.id}`}
                        />
                        <span className="flex-1 min-w-0 truncate">{t.name}</span>
                        {selected && (newMaintenance.assigned_to_ids || [])[0] === t.id && (
                          <span className="text-[10px] font-bold text-blue-700 uppercase">Primario</span>
                        )}
                      </label>
                    );
                  })}
                </div>
                {(newMaintenance.assigned_to_ids || []).length > 0 && (
                  <p className="text-xs text-slate-500">
                    {newMaintenance.assigned_to_ids.length} tecnico/i selezionato/i. Il primo della lista sara il primario.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Note</Label>
                <Textarea value={newMaintenance.notes} onChange={(e) => setNewMaintenance({...newMaintenance, notes: e.target.value})} placeholder="Note per il tecnico..." />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Annulla</Button>
                <Button type="submit" className="bg-blue-900 hover:bg-blue-800">Crea Programmazione</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Custom Calendar Grid */}
        <Card className="lg:col-span-3 border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-lg font-bold">Calendario</CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={prevMonth}><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-xs sm:text-sm font-semibold min-w-[100px] sm:min-w-[140px] text-center capitalize">
                {formatRome(viewMonth, { month: 'long', year: 'numeric' })}
              </span>
              <Button variant="ghost" size="icon" onClick={nextMonth}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </CardHeader>
          <CardContent className="px-3 pb-4">
            {/* Weekday headers */}
            <div className="grid grid-cols-7 mb-1">
              {WEEKDAYS.map(day => (
                <div key={day} className="text-center text-xs font-bold text-slate-400 uppercase tracking-wider py-2">
                  {day}
                </div>
              ))}
            </div>
            {/* Days grid */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map(({ date, isCurrentMonth }, idx) => {
                const selected = isSelected(date);
                const today = isToday(date);
                const events = hasEvents(date);
                const eventCount = maintenanceDateMap[date.toDateString()]?.length || 0;
                return (
                  <button
                    key={idx}
                    onClick={() => setSelectedDate(date)}
                    className={`relative flex flex-col items-center justify-center rounded-xl transition-all h-16 sm:h-18 ${
                      selected
                        ? 'bg-blue-900 text-white shadow-lg'
                        : today
                        ? 'bg-blue-50 text-blue-900 font-bold ring-2 ring-blue-200'
                        : isCurrentMonth
                        ? 'text-slate-700 hover:bg-slate-100'
                        : 'text-slate-300'
                    }`}
                    data-testid={`cal-day-${date.getDate()}-${date.getMonth()}`}
                  >
                    <span className={`text-sm font-semibold ${selected ? 'text-white' : ''}`}>
                      {date.getDate()}
                    </span>
                    {events && (
                      <div className="flex items-center gap-0.5 mt-0.5">
                        <div className={`w-1.5 h-1.5 rounded-full ${selected ? 'bg-white' : 'bg-blue-600'}`} />
                        {eventCount > 1 && (
                          <span className={`text-[9px] font-bold ${selected ? 'text-blue-200' : 'text-blue-600'}`}>
                            {eventCount}
                          </span>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Selected date details */}
        <Card className="lg:col-span-2 border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <CalendarIcon className="w-5 h-5" />
              {formatRome(selectedDate, { weekday: 'long', day: 'numeric', month: 'long' })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[340px]">
              {selectedDateMaintenances.length > 0 ? (
                <div className="space-y-3">
                  {selectedDateMaintenances.map(m => {
                    const p = m.progress || { pct: 0, done: 0, total: 0 };
                    const overdue = new Date(m.scheduled_date) < new Date() && p.pct < 100 && p.total > 0;
                    return (
                      <div
                        key={m.id}
                        className="p-4 bg-slate-50 rounded-lg space-y-2 hover:bg-slate-100 cursor-pointer transition"
                        onClick={() => navigate(`/scheduled/${m.id}`)}
                        data-testid={`maintenance-card-${m.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="font-semibold text-slate-900">{m.title}</h4>
                          <div className="flex gap-1 flex-wrap justify-end">
                            <Badge variant="secondary" className="text-xs">
                              <Repeat className="w-3 h-3 mr-1" /> {frequencyLabels[m.frequency]}
                            </Badge>
                            {m.is_legacy && (
                              <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-400">
                                Vecchio formato
                              </Badge>
                            )}
                            {overdue && (
                              <Badge className="text-[10px] bg-red-600 hover:bg-red-700">In ritardo</Badge>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-slate-500 flex items-center gap-1">
                          <Building2 className="w-3 h-3" /> {getHotelName(m.hotel_id)} - {m.area}
                        </p>
                        <p className="text-sm text-slate-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {formatRome(m.scheduled_date, { hour: '2-digit', minute: '2-digit' })}
                        </p>
                        {p.total > 0 && (
                          <div className="space-y-1 pt-1">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-slate-500 flex items-center gap-1">
                                <ListChecks className="w-3 h-3" /> {p.done}/{p.total} completati
                              </span>
                              <span className={`font-bold tabular-nums ${p.pct === 100 ? 'text-emerald-600' : 'text-blue-700'}`}>{p.pct}%</span>
                            </div>
                            <div className="h-1.5 bg-slate-200 rounded overflow-hidden">
                              <div
                                className={`h-full transition-all ${p.pct === 100 ? 'bg-emerald-500' : 'bg-blue-600'}`}
                                style={{ width: `${p.pct}%` }}
                              />
                            </div>
                          </div>
                        )}
                        <div className="flex gap-2 pt-2" onClick={(e) => e.stopPropagation()}>
                          {p.total > 0 ? (
                            <Button size="sm" className="flex-1 bg-blue-900 hover:bg-blue-800" onClick={() => navigate(`/scheduled/${m.id}`)}>
                              <ListChecks className="w-3 h-3 mr-1" /> Apri checklist
                            </Button>
                          ) : (
                            <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => handleExecute(m.id)}>
                              <Play className="w-3 h-3 mr-1" /> Esegui
                            </Button>
                          )}
                          {isManager && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-blue-700 hover:bg-blue-50"
                              onClick={() => openEdit(m)}
                              title="Modifica / Riassegna"
                              data-testid={`edit-maintenance-${m.id}`}
                            >
                              <UserCog className="w-3 h-3" />
                            </Button>
                          )}
                          {isManager && (
                            <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(m.id)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CalendarIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Nessuna manutenzione programmata</p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg font-bold">Prossime Manutenzioni</CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-64">
            <div className="space-y-3">
              {maintenances
                .filter(m => new Date(m.scheduled_date) >= new Date())
                .sort((a, b) => new Date(a.scheduled_date).getTime() - new Date(b.scheduled_date).getTime())
                .slice(0, 10)
                .map(m => {
                  const p = m.progress || { pct: 0, done: 0, total: 0 };
                  return (
                    <div
                      key={m.id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition"
                      onClick={() => navigate(`/scheduled/${m.id}`)}
                      data-testid={`upcoming-card-${m.id}`}
                    >
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-900">{m.title}</h4>
                        <div className="flex items-center gap-4 mt-1 text-sm text-slate-500 flex-wrap">
                          <span className="flex items-center gap-1"><CalendarIcon className="w-3 h-3" /> {formatRomeDate(m.scheduled_date)}</span>
                          <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {getHotelName(m.hotel_id)}</span>
                          <Badge variant="secondary" className="text-xs">{frequencyLabels[m.frequency]}</Badge>
                          {p.total > 0 && (
                            <span className="flex items-center gap-1 text-xs">
                              <ListChecks className="w-3 h-3" />
                              <span className="font-semibold tabular-nums">{p.done}/{p.total}</span>
                              <span className={`tabular-nums ${p.pct === 100 ? 'text-emerald-600' : 'text-blue-700'}`}>({p.pct}%)</span>
                            </span>
                          )}
                        </div>
                        {p.total > 0 && (
                          <div className="h-1 bg-slate-200 rounded overflow-hidden mt-2 max-w-md">
                            <div className={`h-full ${p.pct === 100 ? 'bg-emerald-500' : 'bg-blue-600'}`} style={{ width: `${p.pct}%` }} />
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
                        {p.total > 0 ? (
                          <Button size="sm" className="bg-blue-900 hover:bg-blue-800" onClick={() => navigate(`/scheduled/${m.id}`)}>
                            <ListChecks className="w-4 h-4 mr-1" /> Apri checklist
                          </Button>
                        ) : (
                          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleExecute(m.id)}>
                            <Play className="w-4 h-4 mr-1" /> Esegui Ora
                          </Button>
                        )}
                        {isManager && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-blue-700 hover:bg-blue-50"
                            onClick={() => openEdit(m)}
                            title="Modifica / Riassegna"
                            data-testid={`edit-upcoming-${m.id}`}
                          >
                            <UserCog className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              {maintenances.filter(m => new Date(m.scheduled_date) >= new Date()).length === 0 && (
                <p className="text-center text-slate-500 py-8">Nessuna manutenzione programmata</p>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Storico Manutenzioni Completate */}
      <Card className="border-slate-200" data-testid="history-card">
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 space-y-0">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-emerald-50 text-emerald-700">
              <Archive className="w-4 h-4" />
            </div>
            <div>
              <CardTitle className="text-lg font-bold">Storico Completate</CardTitle>
              <p className="text-xs text-slate-500 mt-0.5">Manutenzioni eseguite — archivio per audit e controllo</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
            <Select value={historyHotelFilter} onValueChange={setHistoryHotelFilter}>
              <SelectTrigger className="w-full sm:w-[220px] h-9 min-w-0 flex-1 sm:flex-none" data-testid="history-hotel-filter">
                <Building2 className="w-3.5 h-3.5 mr-1.5 text-slate-400 shrink-0" />
                <SelectValue placeholder="Filtra per hotel" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli hotel</SelectItem>
                {hotels.map(h => (
                  <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Badge variant="secondary" className="text-xs shrink-0" data-testid="history-count">
              {history.length} {history.length === 1 ? 'voce' : 'voci'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {historyLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-10 text-slate-500">
              <Archive className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm">Nessuna manutenzione completata{historyHotelFilter !== 'all' && ' per questo hotel'}</p>
              <p className="text-xs text-slate-400 mt-1">Le manutenzioni appariranno qui dopo essere state eseguite al 100%</p>
            </div>
          ) : (
            <ScrollArea className="h-80">
              <div className="space-y-2.5 pr-2">
                {/* Raggruppato per hotel se "Tutti" è selezionato */}
                {(historyHotelFilter === 'all'
                  ? Object.entries(
                      history.reduce((acc, m) => {
                        const k = m.hotel_id || 'unknown';
                        (acc[k] = acc[k] || []).push(m);
                        return acc;
                      }, {})
                    )
                  : [[historyHotelFilter, history]]
                ).map(([hid, items]) => (
                  <div key={hid} className="space-y-1.5">
                    {historyHotelFilter === 'all' && (
                      <div className="flex items-center gap-2 mt-3 mb-1 first:mt-0">
                        <Building2 className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                          {getHotelName(hid)}
                        </span>
                        <span className="text-[11px] text-slate-400">· {items.length}</span>
                        <div className="flex-1 h-px bg-slate-100 ml-2" />
                      </div>
                    )}
                    {items.map(m => {
                      const p = m.progress || { pct: 0, done: 0, total: 0 };
                      const completedAt = m.last_executed ? formatRome(m.last_executed) : '—';
                      const ids = m.assigned_to_ids && m.assigned_to_ids.length > 0
                        ? m.assigned_to_ids
                        : (m.assigned_to ? [m.assigned_to] : []);
                      const techNames = ids
                        .map(uid => technicians.find(t => t.id === uid)?.name)
                        .filter(Boolean);
                      return (
                        <div
                          key={m.id}
                          className="flex items-start sm:items-center justify-between gap-2 sm:gap-3 p-3 rounded-lg border border-emerald-100 bg-emerald-50/40 hover:bg-emerald-50 transition cursor-pointer overflow-hidden"
                          onClick={() => navigate(`/scheduled/${m.id}`)}
                          data-testid={`history-item-${m.id}`}
                        >
                          <div className="flex items-start sm:items-center gap-2 sm:gap-3 min-w-0 flex-1">
                            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5 sm:mt-0" />
                            <div className="min-w-0 flex-1">
                              <div className="font-semibold text-slate-900 truncate">{m.title}</div>
                              <div className="flex items-center gap-x-3 gap-y-1 mt-0.5 text-xs text-slate-500 flex-wrap">
                                <span className="flex items-center gap-1 whitespace-nowrap"><Clock className="w-3 h-3 shrink-0" /> Eseguita: <b className="text-slate-700">{completedAt}</b></span>
                                <Badge variant="secondary" className="text-[10px] shrink-0">{frequencyLabels[m.frequency] || m.frequency}</Badge>
                                {techNames.length > 0 && (
                                  <span className="flex items-center gap-1 min-w-0 max-w-[220px] sm:max-w-none">
                                    <UserCog className="w-3 h-3 shrink-0" />
                                    <span className="truncate">
                                      {techNames.length === 1 ? techNames[0] : `${techNames[0]} +${techNames.length - 1}`}
                                    </span>
                                  </span>
                                )}
                                {p.total > 0 && (
                                  <span className="flex items-center gap-1 text-emerald-700 font-semibold whitespace-nowrap">
                                    <ListChecks className="w-3 h-3 shrink-0" /> {p.done}/{p.total} ({p.pct}%)
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <Badge className="bg-emerald-600 text-white text-[10px] shrink-0 hidden sm:inline-flex">COMPLETATA</Badge>
                          <Badge className="bg-emerald-600 text-white text-[9px] shrink-0 sm:hidden px-1.5 py-0.5">OK</Badge>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Dialog Edit / Riassegna manutenzione */}
      <Dialog open={!!editDialog} onOpenChange={(o) => !o && !savingEdit && setEditDialog(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-maintenance-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="w-5 h-5 text-blue-700" />
              Modifica / Riassegna manutenzione
            </DialogTitle>
          </DialogHeader>
          {editDialog && (
            <div className="space-y-3 py-2">
              <div>
                <Label>Titolo</Label>
                <Input
                  value={editDialog.title}
                  onChange={(e) => setEditDialog({ ...editDialog, title: e.target.value })}
                  data-testid="edit-title-input"
                />
              </div>
              <div>
                <Label>Descrizione</Label>
                <Textarea
                  value={editDialog.description}
                  onChange={(e) => setEditDialog({ ...editDialog, description: e.target.value })}
                  rows={3}
                  data-testid="edit-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Data programmata</Label>
                  <Input
                    type="datetime-local"
                    value={editDialog.scheduled_date}
                    onChange={(e) => setEditDialog({ ...editDialog, scheduled_date: e.target.value })}
                    data-testid="edit-date-input"
                  />
                </div>
                <div>
                  <Label>Priorità</Label>
                  <Select
                    value={editDialog.priority}
                    onValueChange={(v) => setEditDialog({ ...editDialog, priority: v })}
                  >
                    <SelectTrigger data-testid="edit-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bassa">Bassa</SelectItem>
                      <SelectItem value="media">Media</SelectItem>
                      <SelectItem value="alta">Alta</SelectItem>
                      <SelectItem value="urgente">Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <UserCog className="w-4 h-4" /> Manutentori assegnati <span className="text-xs text-slate-500 font-normal">(selezione multipla)</span>
                </Label>
                <div className="border border-slate-200 rounded-md max-h-40 overflow-y-auto p-2 space-y-1 bg-white mt-1" data-testid="edit-assignee-multi-list">
                  {technicians.length === 0 && <p className="text-xs text-slate-400 p-2">Nessun tecnico disponibile</p>}
                  {technicians.map(t => {
                    const selected = (editDialog.assigned_to_ids || []).includes(t.id);
                    return (
                      <label
                        key={t.id}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm ${selected ? 'bg-blue-50 border border-blue-200' : 'hover:bg-slate-50 border border-transparent'}`}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={(e) => {
                            const cur = editDialog.assigned_to_ids || [];
                            const next = e.target.checked ? [...cur, t.id] : cur.filter(x => x !== t.id);
                            setEditDialog({ ...editDialog, assigned_to_ids: next });
                          }}
                          className="rounded border-slate-300"
                          data-testid={`edit-tech-checkbox-${t.id}`}
                        />
                        <span className="flex-1 min-w-0 truncate">
                          {t.name}{t.role === 'capo_manutentore' ? ' (Capo)' : ''}
                        </span>
                        {selected && (editDialog.assigned_to_ids || [])[0] === t.id && (
                          <span className="text-[10px] font-bold text-blue-700 uppercase">Primario</span>
                        )}
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Il primo tecnico selezionato sara il primario. Gli altri tecnici dell&apos;hotel vedranno la manutenzione in sola lettura.
                </p>
              </div>
              <div>
                <Label>Note</Label>
                <Textarea
                  value={editDialog.notes}
                  onChange={(e) => setEditDialog({ ...editDialog, notes: e.target.value })}
                  rows={2}
                  placeholder="Note operative o motivo della riassegnazione (opzionale)"
                  data-testid="edit-notes-input"
                />
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" disabled={savingEdit} onClick={() => setEditDialog(null)} data-testid="edit-cancel">
              Annulla
            </Button>
            <Button
              className="bg-blue-700 hover:bg-blue-800"
              disabled={savingEdit || !editDialog?.title}
              onClick={saveEdit}
              data-testid="edit-save"
            >
              {savingEdit ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Pencil className="w-4 h-4 mr-2" />}
              Salva modifiche
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScheduledMaintenancePage;
